import clsx from 'clsx';
import _ from 'lodash';
import { isValidElement } from 'react';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FakeLink } from '@/core/FakeLink';
import { faFont, faPlus } from '@seeqdev/icons/fa-sharp-regular-svg-icons';
import { fcAllItems } from '@seeqdev/icons/seeq';
import { Button, Icon, InputGroup, Select } from '@seeqdev/qomponents';
import type { StyleXStyles } from '@stylexjs/stylex';
import type { CapsuleSelectionValue } from '@/reportEditor/report.constants';
import type { AnyProperty } from '@/utilities.types';
import type { IconDefinition } from '@seeqdev/icons';

export interface ReactSelectOption<T = string | number | boolean | CapsuleSelectionValue | AnyProperty> {
  text: string;
  value: T | undefined | null;
  icon?: IconDefinition | React.ReactElement;
  iconExtraClassNames?: string;
  label?: string | React.ReactElement;
  divider?: boolean;
  group?: string;
  searchableText?: string;
}

/**
 * Renders a Select input that may contain icons and plain text options.
 */
interface IconSelectProps {
  /** an array of options. The text property will be displayed as the text of the option. Add a label to the object
   *  and set formattedOptions=true to use a custom label. */
  selectOptions: readonly ReactSelectOption[];
  /** function that is called when an option is selected. The first param is an object with a value key, the second
   * param is optional. If a "name" is provided as part of the IconSelectProps the name is passed back as the
   * property via this function. */
  onChange: (option: ReactSelectOption, property?: string) => void;
  /** true if the 'selectOptions' will come in with custom labels, false to use
   * the default formatting here. If true, the selectOptions array must contain a 'label' attribute. */
  formattedOptions?: boolean;
  /** string that is provided to the onChange function as the second parameter */
  name?: string;
  /** additional classNames for Select element. This can be useful for targeting selects in system tests in addition to
   *  styling. */
  extraClassNames?: string;
  /** translation key for placeholder text */
  placeholder?: string;
  /** selected value */
  value?: any;
  id?: string;
  testId?: string;
  /** specify true if element should be disabled */
  disabled?: boolean;
  /** additional classes for parent element of Select */
  wrapperClasses?: string;
  /** additional classes for control element of Select */
  controlClasses?: string;
  controlXstyle?: StyleXStyles;
  skipMemo?: boolean;
  skipTranslation?: boolean;
  isMultipleSelect?: boolean;
  isSearchable?: boolean;
  small?: boolean;
  inputGroup?: 'left' | 'right' | undefined;
  /** defaults to true */
  appendToBody?: boolean;
  showError?: boolean;
  tooltip?: string;
  creatable?: boolean;
  isClearable?: boolean;
  /** optional function for grouping select options **/
  groupOptions?: (options: readonly ReactSelectOption[]) => readonly object[];
  /** optional function to format group labels **/
  formatGroupLabel?: (data: any) => any;
  removeAllEnabled?: boolean;
  onRemoveAllClick?: () => void;
  removeAllLinkClassNames?: string;
  addItemsEnabled?: boolean;
  addItemsLabel?: string;
  onAddItemsClick?: () => void;
  // true if the select can be toggled between a text input field and a select.
  textEntryAllowed?: boolean;
  textToggleDisabled?: boolean;
  /** placeholder for the text input when in text-entry mode (overrides the default) */
  textInputPlaceholder?: string;
  /** custom filter function for searching options */
  filterOption?: (option: any, rawInput: string) => boolean;
  inputToggleTooltip?: string;
  /** when true, displays a loading spinner inside the menu (e.g. while fetching the next page) */
  isLoading?: boolean;
  /** callback invoked when the user scrolls to the bottom of the menu - use for infinite-scroll loading */
  onMenuScrollToBottom?: (event: WheelEvent | TouchEvent) => void;
}

const IconSelectUnwrapped = (props: IconSelectProps) => {
  const {
    name,
    value,
    selectOptions,
    formattedOptions,
    extraClassNames,
    testId,
    placeholder,
    onChange,
    appendToBody = true,
    disabled = false,
    wrapperClasses = '',
    controlClasses = '',
    controlXstyle,
    isMultipleSelect = false,
    isSearchable = false,
    small,
    inputGroup,
    showError,
    tooltip,
    creatable = false,
    isClearable,
    groupOptions,
    formatGroupLabel,
    skipTranslation,
    removeAllEnabled = false,
    onRemoveAllClick,
    removeAllLinkClassNames,
    addItemsEnabled = false,
    addItemsLabel,
    onAddItemsClick,
    textEntryAllowed = false,
    textToggleDisabled = true,
    textInputPlaceholder,
    filterOption,
    inputToggleTooltip = 'USE_PROPERTY_SELECTOR',
    isLoading,
    onMenuScrollToBottom,
  } = props;
  const { t } = useTranslation();

  const initialInputIsTextInput =
    textEntryAllowed && typeof value === 'string' && !selectOptions.find((opt) => opt.value === value);

  const [displayTextEntry, setDisplayTextEntry] = useState(initialInputIsTextInput);
  const [textInputText, setTextInputText] = useState(initialInputIsTextInput ? (value as string) : '');
  const [savedDropdownValue, setSavedDropdownValue] = useState<ReactSelectOption | undefined>(
    initialInputIsTextInput ? undefined : value,
  );

  const inputValueOnBlur = useRef<ReactSelectOption | undefined>(undefined);

  const makeLabel = (label: string, icon: ReactSelectOption['icon'], iconExtraClassNames?: string) => {
    const content = skipTranslation ? label : t(label);

    if (_.isUndefined(icon)) {
      return content;
    }

    return (
      <span>
        {isValidElement(icon) ? (
          icon
        ) : (
          <Icon
            icon={icon}
            // `.specSelectControl svg` dims every svg in a select to `opacity: 0.5` so we need to override it for this icon.
            extraClassNames={clsx('tw:p-0.5 tw:opacity-100!', { mr5: !!label }, iconExtraClassNames)}
            type="text"
          />
        )}
        {content}
      </span>
    );
  };

  const options = formattedOptions
    ? selectOptions
    : _.map(selectOptions, (option) => ({
        ...option,
        label: makeLabel(option.text, option?.icon, option?.iconExtraClassNames),
      }));

  const groupedOptions = groupOptions ? groupOptions(options) : options;

  const getSelectedValue = (selectedValue: string | ReactSelectOption | null | undefined) => {
    if (_.isUndefined(selectedValue)) {
      return undefined;
    }
    const valueToFind =
      !_.isNil(selectedValue) && typeof selectedValue === 'object' && 'value' in selectedValue
        ? selectedValue.value
        : selectedValue;
    return _.find(options as ReactSelectOption[], (option) => _.isEqual(option.value, valueToFind));
  };

  const toggleTextEntryDisplay = () => {
    if (textToggleDisabled) {
      return;
    }
    if (!displayTextEntry) {
      // Switching to text mode — save current dropdown selection, notify parent with text value
      setSavedDropdownValue(value);
      if (textInputText) {
        onChange({ value: textInputText, text: textInputText });
      }
    } else {
      // Switching to dropdown mode — restore saved dropdown selection
      const savedOption = getSelectedValue(savedDropdownValue);
      if (savedOption) {
        onChange(savedOption, name);
      }
    }
    setDisplayTextEntry(!displayTextEntry);
  };

  const selectedValueForDisplay = isMultipleSelect
    ? _.map(value, (eachSelectedValue) => getSelectedValue(eachSelectedValue))
    : getSelectedValue(value);

  const removeAllLink = (
    <div
      className={
        removeAllLinkClassNames || 'flexColumnContainer flexAlignBottom flexFill tw:justify-end tw:pt-sq-5 tw:pb-sq-5'
      }>
      <FakeLink testId="removeAllLink" onClick={() => (onRemoveAllClick ? onRemoveAllClick() : _.noop)}>
        {t('DETAILS_SELECTION.REMOVE_ALL')}
      </FakeLink>
    </div>
  );

  const addItemsButton =
    addItemsLabel && onAddItemsClick ? (
      <div className="flexColumnContainer flexAlignBottom tw:pt-sq-5 tw:pb-sq-5">
        <Button
          testId="addItemsButton"
          icon={faPlus}
          variant="theme"
          size="xs"
          label={addItemsLabel}
          onClick={onAddItemsClick}
        />
      </div>
    ) : null;

  const renderTextInputToggle = (
    <Button
      variant="no-border"
      tooltip={t(displayTextEntry ? inputToggleTooltip : 'USE_VALUE_ENTRY')}
      icon={displayTextEntry ? faFont : fcAllItems}
      testId="item-select-toggle"
      onClick={toggleTextEntryDisplay}
      extraClassNames={clsx('flexRowContainer tw:items-center tw:justify-center', {
        disabledBehavior: textToggleDisabled,
      })}
      disabled={textToggleDisabled}
    />
  );

  const showPlaceholderText =
    textEntryAllowed && !displayTextEntry && !selectOptions.find((opt) => opt.value === value);
  const renderSelect = (
    <Select
      extraClassNames={clsx(extraClassNames ?? '', !displayTextEntry ? 'flexFill' : '')}
      controlClassNames={controlClasses}
      controlXstyle={controlXstyle}
      placeholder={_.isEmpty(placeholder) ? '' : t(placeholder as string)}
      // only null resets the select to show the placeholder
      value={showPlaceholderText || _.isUndefined(selectedValueForDisplay) ? null : selectedValueForDisplay}
      options={groupedOptions as any}
      formatGroupLabel={formatGroupLabel}
      isSearchable={isSearchable}
      onChange={(selectedOption) => {
        if (!selectedOption) {
          onChange(selectedOption, name);
          return;
        }
        if (!displayTextEntry && !Array.isArray(selectedOption)) {
          setSavedDropdownValue(selectedOption);
        }
        onChange(selectedOption, name);
      }}
      isDisabled={disabled}
      isMulti={isMultipleSelect}
      small={small}
      inputGroup={inputGroup}
      menuPortalTarget={appendToBody ? document.body : undefined}
      showError={showError}
      creatable={creatable}
      isClearable={isClearable}
      filterOption={filterOption}
      isLoading={isLoading}
      onMenuScrollToBottom={onMenuScrollToBottom}
      onInputChange={(inputValue, action) => {
        if (inputValue) {
          inputValueOnBlur.current = { value: inputValue, text: inputValue, label: inputValue };
        }
        if (action.action === 'input-blur' && inputValueOnBlur.current) {
          onChange(inputValueOnBlur.current as ReactSelectOption, name);
        }
        if (action.action === 'menu-close') {
          inputValueOnBlur.current = undefined;
        }
      }}
    />
  );

  const renderSelectGroup = (
    <InputGroup
      field={renderSelect}
      extraClassNames="no-border forceZeroPadding"
      testId={`${testId}-select`}
      append={[
        {
          variant: 'element',
          element: renderTextInputToggle,
        },
      ]}
    />
  );

  const renderTextInput = (
    <InputGroup
      type="text"
      value={textInputText}
      onChange={(e) => {
        setTextInputText(e.target.value);
      }}
      onBlur={() => {
        onChange({ value: textInputText, text: textInputText });
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          event.stopPropagation();
          event.currentTarget.blur();
        }
      }}
      testId={`${testId}-input`}
      extraClassNames={clsx(extraClassNames)}
      size="sm"
      placeholder={t(textInputPlaceholder ?? 'ITEM_FINDER.PROPERTY_NAME_ENTER_PLACEHOLDER')}
      append={
        textEntryAllowed
          ? [
              {
                variant: 'element',
                element: renderTextInputToggle,
              },
            ]
          : []
      }
    />
  );

  return (
    <div
      data-testid={testId || `${name}_filter`}
      className={wrapperClasses}
      onClick={(e) => e.stopPropagation()}
      data-qtip-text={tooltip ? t(tooltip) : undefined}>
      {textEntryAllowed && (
        <div className="flexColumnContainer tw:mt-sq-5 tw:flex-1 tw:items-center">
          {!displayTextEntry ? renderSelectGroup : renderTextInput}
        </div>
      )}
      {!textEntryAllowed && renderSelect}
      <div className="tw:flex tw:items-center tw:justify-end tw:gap-2">
        {addItemsEnabled && !disabled && addItemsButton}
        {removeAllEnabled && !_.isEmpty(value) && !disabled && removeAllLink}
      </div>
    </div>
  );
};

export const IconSelect = React.memo(
  IconSelectUnwrapped,
  (prev, next) =>
    !(
      prev.skipMemo ||
      prev.skipTranslation ||
      prev.extraClassNames !== next.extraClassNames ||
      prev.controlXstyle !== next.controlXstyle ||
      prev.disabled !== next.disabled ||
      prev.placeholder !== next.placeholder ||
      prev.isLoading !== next.isLoading ||
      prev.onMenuScrollToBottom !== next.onMenuScrollToBottom ||
      !_.isEqualWith(prev.selectOptions, next.selectOptions, (o1, o2) => !_.isEqual(o1.value, o2.value)) ||
      !_.isEqual(prev.value, next.value)
    ),
);
